import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { FileText, Pencil, Plus, Trash2 } from "lucide-react";
import { ActionOverflowMenu, ActionOverflowMenuItem } from "@doculegis/shared";
import { GlassCard } from "@/components/common/GlassCard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { PageMobileActions } from "@/components/common/PageMobileActions";
import { Button } from "@/components/ui/button";
import { ContractTypeSheet, type ContractTypeSheetDraft } from "@/components/contract-types/ContractTypeSheet";
import { UploadStatusText } from "@/components/form/InstructionWordLimit";
import { uploadReadiness } from "@/lib/reference-summary-file";
import { useSummarizeJobList } from "@/lib/summarize-jobs";
import { useContractTypes } from "@/hooks/useSupabase";
import { pageActionButtonClassName, pageActionDesktopOnlyClassName, pageActionLabelClassName } from "@/lib/page-action-button";
import { usePermission } from "@/contexts/AuthContext";
import type { ContractTypeRecord } from "@/types";
import { InlineAssistantLayout } from "@/components/assistant/InlineAssistantLayout";
import { ContractTypeAssistantPanel } from "../components/contract-type-assistant/ContractTypeAssistantPanel";

export function ContractTypesPage() {
  const { contractTypes, deleteUserType, isLoading, error } = useContractTypes();
  const canCreate = usePermission("contractType", "create");
  const canUpdate = usePermission("contractType", "update");
  const canDelete = usePermission("contractType", "delete");
  const canDeleteSystem = usePermission("contractType", "deleteSystem");
  const showAssistant = canCreate || canUpdate;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDraft, setSheetDraft] = useState<ContractTypeSheetDraft | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ContractTypeRecord | null>(null);
  const [removing, setRemoving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ContractTypeRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [highlightTypeId, setHighlightTypeId] = useState<string | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const summarizeJobs = useSummarizeJobList();

  useEffect(() => {
    if (!highlightTypeId) return;
    const timer = window.setTimeout(() => setHighlightTypeId(null), 3000);
    return () => window.clearTimeout(timer);
  }, [highlightTypeId]);

  const editableRows = useMemo(() => contractTypes.filter(item => !item.is_system), [contractTypes]);
  const systemRows = useMemo(() => contractTypes.filter(item => item.is_system), [contractTypes]);

  function openCreate() {
    setSheetDraft({
      mode: "create",
      name: "",
      value: "",
      guidelines: "",
    });
    setSheetOpen(true);
  }

  function openCustomize(item: ContractTypeRecord) {
    setSheetDraft({
      mode: "customize",
      name: item.name,
      value: item.value,
      guidelines: item.guidelines || "",
    });
    setSheetOpen(true);
  }

  function openEdit(item: ContractTypeRecord) {
    setSheetDraft({
      mode: "edit",
      id: item.id,
      name: item.name,
      value: item.value,
      guidelines: item.guidelines || "",
    });
    setSheetOpen(true);
  }

  async function handleRemoveFromOrg() {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await deleteUserType(removeTarget.id);
      setRemoveTarget(null);
    } finally {
      setRemoving(false);
    }
  }

  async function handleDeleteCustom() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteUserType(deleteTarget.id);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section
      className={cn(
        "relative p-6 md:p-8",
        assistantOpen && "flex min-h-0 flex-1 flex-col overflow-hidden"
      )}
    >
      <div className="mb-5 flex shrink-0 flex-row items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-[var(--primary-soft-border)] bg-[var(--primary-soft-bg)] text-[#7c3aed] sm:h-10 sm:w-10 dark:border-violet-300/30 dark:bg-violet-500/15 dark:text-violet-200">
            <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
              Contract Types
            </h1>
            <p className="hidden text-sm text-[var(--lm-muted)] sm:block dark:text-slate-300/85">
              Manage system defaults and your custom review templates.
            </p>
          </div>
        </div>
        {canCreate ? (
          <Button
            type="button"
            onClick={openCreate}
            title="New contract type"
            aria-label="New contract type"
            className={`${pageActionButtonClassName} ${pageActionDesktopOnlyClassName}`}
          >
            <Plus className="h-4 w-4" />
            <span className={pageActionLabelClassName}>New contract type</span>
          </Button>
        ) : null}
      </div>

      {canCreate ? (
        <PageMobileActions
          actions={[
            {
              id: "new-contract-type",
              icon: Plus,
              label: "New contract type",
              onClick: openCreate,
            },
          ]}
        />
      ) : null}

      <InlineAssistantLayout
        open={assistantOpen}
        onOpenChange={setAssistantOpen}
        showTrigger={showAssistant}
        triggerLabel="Contract Type Assistant"
        assistant={
          <ContractTypeAssistantPanel
            className="h-full min-h-0"
            onClose={() => setAssistantOpen(false)}
            onContractTypeWritten={setHighlightTypeId}
          />
        }
      >
      <GlassCard className="overflow-hidden border-[var(--lm-border-card)] bg-white p-0 shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none">
        <h2 className="border-b border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-4 py-3 text-[11px] font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:border-white/10 dark:bg-transparent dark:text-slate-400">
          All contract types
        </h2>
        {isLoading ? <p className="px-4 py-3.5 text-sm text-[var(--lm-muted)] dark:text-slate-300/85">Loading...</p> : null}
        {error ? <p className="px-4 py-3.5 text-sm text-[var(--tint-red-fg)] dark:text-red-300">{error}</p> : null}

        <div className="divide-y divide-[var(--lm-border-subtle)] dark:divide-white/10">
          {systemRows.map(item => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-3 px-4 py-3.5"
            >
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-100">{item.name}</p>
                <span className="ml-2 inline-flex shrink-0 rounded-full bg-[var(--tint-purple-bg)] px-2 py-0.5 text-[10px] font-medium text-[var(--tint-purple-fg)] dark:bg-indigo-500/15 dark:text-indigo-200">
                  System
                </span>
              </div>
              <ActionOverflowMenu>
                {canUpdate ? (
                  <ActionOverflowMenuItem
                    icon={Pencil}
                    label="Customize for my account"
                    onClick={() => openCustomize(item)}
                  />
                ) : null}
                {canDeleteSystem ? (
                  <ActionOverflowMenuItem
                    icon={Trash2}
                    label="Remove from organization"
                    variant="destructive"
                    onClick={() => setRemoveTarget(item)}
                  />
                ) : null}
              </ActionOverflowMenu>
            </div>
          ))}

          {editableRows.map(item => (
            <div
              key={item.id}
              className={cn(
                "flex items-center justify-between gap-3 px-4 py-3.5 transition-colors",
                item.id === highlightTypeId &&
                  "bg-violet-500/10 ring-1 ring-inset ring-violet-500/35 dark:bg-violet-500/15"
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                <p className="min-w-0 truncate text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-100">{item.name}</p>
                <UploadStatusText
                  status={uploadReadiness(
                    summarizeJobs
                      .filter((job) => job.scope === "contract-type" && job.ownerId === item.id)
                      .map((job) => job.status)
                  )}
                />
              </div>
              <ActionOverflowMenu>
                {canUpdate ? (
                  <ActionOverflowMenuItem
                    icon={Pencil}
                    label="Edit"
                    onClick={() => openEdit(item)}
                  />
                ) : null}
                {canDelete ? (
                  <ActionOverflowMenuItem
                    icon={Trash2}
                    label="Delete"
                    variant="destructive"
                    onClick={() => setDeleteTarget(item)}
                  />
                ) : null}
              </ActionOverflowMenu>
            </div>
          ))}
        </div>
      </GlassCard>
      </InlineAssistantLayout>

      <ContractTypeSheet
        open={sheetOpen}
        onOpenChange={v => {
          setSheetOpen(v);
          if (!v) setSheetDraft(null);
        }}
        draft={sheetDraft}
      />

      <ConfirmDialog
        isOpen={removeTarget != null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemoveFromOrg}
        title="Remove from organization"
        description={
          removeTarget
            ? `Remove "${removeTarget.name}" from this organization? Other organizations will still see this system default.`
            : ""
        }
        confirmLabel="Remove from organization"
        variant="destructive"
        isLoading={removing}
      />

      <ConfirmDialog
        isOpen={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteCustom}
        title="Delete contract type"
        description={
          deleteTarget
            ? `Delete "${deleteTarget.name}"? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleting}
      />

    </section>
  );
}
